import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import type {SentEmail} from "../types/sentEmail";
import {getSentEmails} from "../api/sentEmailsApi";

type AsyncStatus = "idle" | "loading" | "succeeded" | "failed";

export type SentEmailsState = {
  list: SentEmail[];
  currentTenantId: string | null;
  /** The latest request; any earlier one answering later (even for the same tenant) is dropped. */
  latestRequestId: string | null;
  status: AsyncStatus;
  error: string | null;
};

const initialState: SentEmailsState = {
  list: [],
  currentTenantId: null,
  latestRequestId: null,
  status: "idle",
  error: null,
};

export const fetchSentEmailsByTenant = createAsyncThunk<
  { emails: SentEmail[]; tenantId: string },
  string
>("sentEmails/fetchByTenant", async (tenantId) => {
  const emails = await getSentEmails(tenantId);
  return {emails, tenantId};
});

export const sentEmailsSlice = createSlice({
  name: "sentEmails",
  initialState,
  reducers: {
    reset(state) {
      state.list = [];
      state.currentTenantId = null;
      state.latestRequestId = null;
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchSentEmailsByTenant.pending, (state, action) => {
        state.status = "loading";
        state.error = null;
        state.latestRequestId = action.meta.requestId;
      })
      .addCase(fetchSentEmailsByTenant.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestRequestId) return;
        state.status = "succeeded";
        state.list = action.payload.emails;
        state.currentTenantId = action.payload.tenantId;
      })
      .addCase(fetchSentEmailsByTenant.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestRequestId) return;
        state.status = "failed";
        state.error = action.error.message ?? "Failed to load sent emails";
      });
  },
});

export const {reset} = sentEmailsSlice.actions;
